/* eslint-disable react/prop-types */
import React from "react";
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@apollo/client";

import JobOwnerSelect, { JOB_OWNER_HELP_TEXT } from "./JobOwnerSelect";
import { JOB_OWNER_QUERY, SET_JOB_OWNER_MUTATION } from "../graphql/jobs_admin";
import { ownerName, ownerUserIdToSend } from "../helpers/jobOwner";

/**
 * Shows a job's owner and lets the user hand it to someone else, through
 * setJobOwner so the job's version (and its matching) is left alone. Saving is
 * only possible once a different owner is picked, so the API is never asked
 * to re-validate the current one.
 */
function ChangeJobOwnerModal({ jobId, onClose, onSaved }) {
  const toast = useToast();
  const [selected, setSelected] = React.useState(null);

  const { data, loading, error } = useQuery(JOB_OWNER_QUERY, {
    variables: { id: jobId },
    skip: !jobId,
    fetchPolicy: "network-only",
  });
  const job = data?.moblyzeJob;
  const currentOwner = job?.owner || null;

  React.useEffect(() => {
    setSelected(currentOwner);
  }, [currentOwner]);

  const [setJobOwner, { loading: saving }] = useMutation(SET_JOB_OWNER_MUTATION);

  const ownerUserId = ownerUserIdToSend(selected, currentOwner?.id);

  const handleSave = async () => {
    if (!ownerUserId) return;
    try {
      const result = await setJobOwner({ variables: { moblyzeJobId: jobId, ownerUserId } });
      const newOwner = result?.data?.setJobOwner?.owner || selected;
      toast({
        title: "Owner updated",
        description: `${ownerName(newOwner)} now owns this job.`,
        status: "success",
      });
      onSaved?.(newOwner);
      onClose();
    } catch (e) {
      toast({
        title: "Could not change the owner",
        description: e.message,
        status: "error",
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={!!jobId} onClose={onClose} size="md" variant="darkMode">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Change owner</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {job?.category && (
            <Text fontSize="sm" color="gray.400" mb={3}>
              {job.category}
            </Text>
          )}
          {error ? (
            <Text color="orange.300">Could not load this job: {error.message}</Text>
          ) : (
            <FormControl>
              <FormLabel htmlFor="changeJobOwner">Owner</FormLabel>
              <JobOwnerSelect
                inputId="changeJobOwner"
                value={selected}
                onChange={setSelected}
                isDisabled={loading}
              />
              <FormHelperText color="gray.400">{JOB_OWNER_HELP_TEXT}</FormHelperText>
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isDisabled={!ownerUserId}
            isLoading={saving}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export { ChangeJobOwnerModal };
export default ChangeJobOwnerModal;
